<template>
  <div
    class="layout relative"
    data-testid="admin-sidebar"
  >
    <layout-header
      :data="me"
      @open-config="isConfigOpen = true"
     data-testid="layouts-default-layout-header"/>
    <div class="layout__row" data-testid="layouts-default-div">
      <div class="layout__item" data-testid="layouts-default-div-2">
        <uiInput
          v-model="searchValue"
          type="text"
          icon-color="#A6AEB8"
          search
          height="36px"
          :placeholder="$t('placeholder.searchByModules')"
          class="module-search"
          data-testid="admin-sidebar-search-input"
        />
      </div>
      <template v-for="(filteredLink, index) in filteredLinks" :key="index">
        <router-link
          v-if="!filteredLink.section && !filteredLink.isHidden"
          class="layout__item"
          :to="filteredLink.to"
          :data-testid="navTestId(filteredLink.to)"
          @click="handleRouterClick(filteredLink)"
        >
          <UiIcon
            color="#FFFFFF"
            :value="filteredLink.icon"
            :width="filteredLink.iconWidth"
            :height="filteredLink.iconHeight"
           data-testid="layouts-default-ui-icon"/>
          <div class="layout__item-name" data-testid="layouts-default-div-3">
            {{ $t(filteredLink.translationKey) }}
          </div>
        </router-link>
        <div v-else-if="filteredLink.section && !filteredLink.isHidden" class="layout__section" data-testid="layouts-default-div-4">
          <router-link
            v-if="filteredLink.to"
            class="layout__item layout__item--section"
            :to="filteredLink.to"
            :data-testid="navTestId(filteredLink.to)"
            @click="handleRouterClick(filteredLink)"
          >
            <UiIcon
              color="#FFFFFF"
              :value="filteredLink.icon"
              :width="filteredLink.iconWidth"
              :height="filteredLink.iconHeight"
             data-testid="layouts-default-ui-icon-2"/>
            <div class="layout__item-name" data-testid="layouts-default-div-5">
              {{ $t(filteredLink.translationKey) }}
            </div>
          </router-link>
          <div
            v-else
            class="layout__item layout__item--section layout__item--section-disabled"
           data-testid="layouts-default-div-6">
            <UiIcon
              color="#FFFFFF"
              :value="filteredLink.icon"
              :width="filteredLink.iconWidth"
              :height="filteredLink.iconHeight"
             data-testid="layouts-default-ui-icon-3"/>
            <div class="layout__item-name" data-testid="layouts-default-div-7">
              {{ $t(filteredLink.translationKey) }}
            </div>
          </div>
          <div class="layout__children" data-testid="layouts-default-div-8">
            <router-link
              v-for="(child, childIndex) in filteredLink.children"
              :key="child.to"
              :class="[
                'layout__item',
                'layout__item--child',
                'layout__child-connector-wrapper',
                childIndex === 0 ? 'first-child' : 'not-first'
              ]"
              :to="child.to"
              :data-testid="navTestId(child.to)"
              @click="handleRouterClick(child)"
            >
              <div class="layout__item-name" data-testid="layouts-default-div-9">
                {{ $t(child.translationKey) }}
              </div>
            </router-link>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="canSeeSupport"
      class="absolute bottom-7 left-1/2 -translate-x-1/2"
     data-testid="layouts-default-div-10">  
      <a
        class="outline-none relative h-[36px] flex items-center gap-x-[6px]  rounded-[44px] pl-2 pr-3 w-max bg-[#0166BB]"
        href="https://skifpro.kaiten.ru/sd"
        target="_blank"
       data-testid="layouts-default-a">
        <SkSupportIcon  data-testid="layouts-default-sk-support-icon"/>

        <div class="text-sm text-white leading-[140%]" data-testid="layouts-default-div-11">
          {{ $t('settings.main.support') }}
        </div>
      </a>
    </div>
    <ModalConfig v-model="isConfigOpen"  data-testid="layouts-default-modal-config"/>

    <!-- Old helpdesk support modal (replaced by Kaiten)
    <transition name="fade">
      <SupportModal
        v-if="isSupport"
        @onClose="isSupport = false"
      />
    </transition>
    -->
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

interface SidebarChild {
  to: string;
  translationKey: string;
}

interface SidebarLink {
  to?: string;
  icon?: string;
  translationKey: string;
  isHidden: boolean;
  iconWidth?: string;
  iconHeight?: string;
  section?: boolean;
  children?: SidebarChild[];
}

import { userStore } from '@/store/modules/user.js';
import { allRoles } from '@/services/roles/roles.js';
import { commandsTemplatesStore } from '@/store/modules/commandsТemplates.js';
import { integratorsStore } from '@/store/modules/integrators';
// import { supportStore } from '@/store/modules/support';
import { searchStore } from '@/store/modules/globalSearch';

import LayoutHeader from '@/components/layouts/navigationDrawer/header.vue';
import UiInput from '@/components/ui/input.vue';
import UiIcon from '@/components/ui/icon.vue';
import ModalConfig from '@/components/modals/config.vue';
import SkSupportIcon from '@/components/icons/SkSupportIcon.vue';
// import SupportModal from '@/modules/support/SupportModal.vue'
import UiTooltip from '@/components/ui/tooltip.vue';

const store = userStore();
const storeTemplates = commandsTemplatesStore();
const storeIntegrators = integratorsStore();
const storeGlobalSearch = searchStore();
// const storeSupport = supportStore();
const router = useRouter();
const role = ref('');
const isConfigOpen = ref(false)

const { t, locale } = useI18n();
const me = computed(() => store.me);
const isUseBilling = computed(() => store.config.use_billing)
const searchValue = ref('');
// const isSupport = ref(false)

const links = ref<SidebarLink[]>([
  {
    to: '/integrators',
    icon: 'star',
    translationKey: 'side_bar.integrators',
    isHidden: false,
    iconWidth: '18px',
    iconHeight: '18px',
    section: true,
    children: [
      { to: '/employees', translationKey: 'side_bar.employees' },
      { to: '/command-templates', translationKey: 'side_bar.command_templates' },
      { to: '/rebroadcasts', translationKey: 'side_bar.rebroadcasts' },
      { to: '/rebroadcasts-history-data', translationKey: 'side_bar.rebroadcastsHD' }
    ]
  },
  {
    to: '/company',
    icon: 'companies',
    translationKey: 'side_bar.companies',
    isHidden: false,
    iconWidth: '18px',
    iconHeight: '18px',
    section: true,
    children: [
      { to: '/users', translationKey: 'side_bar.users' },
      { to: '/drivers', translationKey: 'side_bar.drivers' },
      { to: '/resources/layers', translationKey: 'side_bar.layers' },
      { to: '/object', translationKey: 'side_bar.objects' },
      { to: '/remote-objects', translationKey: 'side_bar.remote_objects' },
      { to: '/terminals', translationKey: 'side_bar.terminals' }
    ]
  },
  {
    to: '/controllers',
    icon: 'users',
    translationKey: 'side_bar.controllers',
    isHidden: false,
    iconWidth: '22px',
    iconHeight: '22px'
  },
  {
    icon: 'payments',
    translationKey: 'side_bar.finances',
    isHidden: false,
    iconWidth: '18px',
    iconHeight: '18px',
    section: true,
    children: [
      { to: '/invoices', translationKey: 'side_bar.accounts' }
    ]
  },
  {
    icon: 'eye-search',
    translationKey: 'side_bar.tools',
    isHidden: false,
    iconWidth: '18px',
    iconHeight: '18px',
    section: true,
    children: [
      { to: '/global-search', translationKey: 'side_bar.search' },
      { to: '/tools/export', translationKey: 'side_bar.export' }
    ]
  }
]);

// const anyUnreadMessages = computed(() => storeSupport.getAnyUnreadMessages)
// const helpdeskError = computed(() => storeSupport.helpdeskError)

const getRoles = computed(() => {
  return store.getRoles;
});

const canSeeSupport = computed(() => isSupervisorOrHigher.value)

const isSupervisorOrHigher = computed(() => {
  return getRoles.value.some(el => el.key === 'SUPERVISOR' || el.key === 'TARIFF_EDITOR');
})

const isController = computed(() => {
  return getRoles.value.some(el => el.key === 'CONTROLLER');
});

const filteredLinks = computed((): SidebarLink[] => {
  const searchTerm = searchValue.value.toLowerCase();
  const visible = links.value
    .map((link) => {
      if (link.translationKey !== 'side_bar.finances' || !link.children) {
        return link;
      }
      const k = store.getCurrentUserRole?.key;
      return {
        ...link,
        children: link.children.filter(
          (child) =>
            child.to !== '/invoices' ||
            k === allRoles.TARIFF_EDITOR ||
            k === allRoles.CONTROLLER
        )
      };
    })
    .filter((link) => {
      if (link.translationKey === 'side_bar.controllers' && !isController.value) {
        return false;
      }
      return !link.isHidden;
    });
  if (searchTerm === '') {
    return visible;
  }
  return visible.filter((link) => {
    if (link.section && link.children) {
      const parentMatch = t(link.translationKey).toLowerCase().includes(searchTerm);
      const childMatch = link.children.some((child: SidebarChild) =>
        t(child.translationKey).toLowerCase().includes(searchTerm)
      );
      return parentMatch || childMatch;
    }
    return t(link.translationKey).toLowerCase().includes(searchTerm);
  });
});

const updateLinksTranslation = () => {
  links.value = links.value.map(link => ({
    ...link,
    translation: t(link.translationKey)
  }));
};

watch(locale, updateLinksTranslation, { immediate: true });
watch(searchValue, () => updateLinksTranslation());

store.getMe().then(async (res) => {
  locale.value = res.lang.key;
  role.value = res.role.key;

  if (res?.need_change_password) {
    router.push('/auth/changePassword');
  } else {
    store.getAllProperties();
    storeTemplates.getDictionaries();
    storeIntegrators.postIntegratorInfo({}, false);
    storeIntegrators.getAllIntegratorsList({});

  }

  store.getAllConfigs().then(() => {
    const financeSection = links.value.find((link) => link.translationKey === 'side_bar.finances');

    if (financeSection && financeSection.children) {
      const hasPayments = financeSection.children.some(child => child.to === '/payments');

      if (isUseBilling.value) {
        if (!hasPayments) {
          financeSection.children.push({
            to: '/payments',
            translationKey: 'side_bar.payments'
          });
        }
      } else if (hasPayments) {
        financeSection.children = financeSection.children.filter(child => child.to !== '/payments');
      }
    }
  });

  // await storeSupport.getKey(res.email);

  // console.log(helpdeskError.value)

  // if (!helpdeskError.value) {
  //   await storeSupport.getSupportUsers()
  //   await storeSupport.getTickets();
  // }

});

const clearStorageSideFilters = () => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('filter')) {
      localStorage.removeItem(key);
    }
  }
};

const NAV_TEST_IDS: Record<string, string> = {
  '/users': 'admin-nav-users',
  '/drivers': 'admin-nav-drivers',
  '/global-search': 'admin-nav-global-search',
  '/tools/export': 'admin-nav-export',
  '/company': 'admin-nav-companies',
  '/integrators': 'admin-nav-integrators',
};

const navTestId = (to?: string) => {
  if (!to) return undefined;
  if (NAV_TEST_IDS[to]) return NAV_TEST_IDS[to];
  const slug = to.replace(/^\//, '').replace(/\//g, '-');
  return `admin-nav-${slug}`;
};

const handleRouterClick = (link) => {
  storeGlobalSearch.setFilterActive(false);
  clearStorageSideFilters();
  if (searchValue.value) {
    searchValue.value = '';
  }
};
</script>


<style scoped lang="scss">
.module-search {
  color: white;
  background-color: #343434;
  border-color: #343434;

  :deep(.input) {
    color: white !important;
    background-color: #343434;
  }

  :deep(input) {
    color: white !important;
    background-color: #343434;
  }
}

.layout {
  display: flex;
  flex-direction: column;
  width: 320px;
  min-width: 320px;
  height: 100vh;
  background-color: #292929;
  box-sizing: border-box;

  &__row {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    row-gap: 4px;
    padding: 12px 10px 80px;
    overflow-y: auto;
    box-sizing: border-box;
  }

  &__item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 11px 0 12px 10px;
    width: 100%;
    transition: 0.3s;
    box-sizing: border-box;
    column-gap: 8px;
    cursor: pointer;

    svg {
      transition: 0.3s all;
    }

    &:hover {
      background: #343434;
      border-radius: 8px;

      svg {
        color: #5c9deb !important;
      }
    }

    &:hover > .layout__item-name {
      color: #5c9deb;
    }

    &-name {
      font-weight: 600;
      font-size: 16px;
      line-height: 120%;
      color: #fff;
      transition: 0.3s;
    }
  }

  &__section {
    width: 100%;
  }

  &__item--section-disabled {
    cursor: default;

    &:hover {
      background: transparent;
      svg {
        color: #FFF !important;
      }
    }

    &:hover > .layout__item-name {
      color: #fff;
    }
    
  }

  &__children {
    display: flex;
    flex-direction: column;
    row-gap: 2px;
  }

  &__item--child {
    padding: 5px 0 5px 30px;
    min-height: auto;
    column-gap: 8px;

    .layout__item-name {
      font-size: 14px;
      padding-left: 10px;
      color: #FFFFFF66
    }
  }
 
  &__child-connector {
    flex-shrink: 0;
    color: #828d9b;
    transition: color 0.2s;
    position: relative;
    top: -7px;
    left: 9px;
  }

  &__item--child.router-link-active .layout__child-connector {
    color: #5c9deb;
  }

  .router-link-active {
    .layout__item-name {
      color: #5c9deb;
    }

    svg {
      color: #5c9deb !important;
    }
  }

  .active {
    .layout__item-name {
      color: #5c9deb;
    }

    svg {
      color: #5c9deb !important;
    }
  }

  &-support__notification {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #D45050;
    position: absolute;
    right: 0;
    top: 0;
  }
}
.layout__child-connector-wrapper {
  position: relative;
}

.layout__child-connector-wrapper.not-first::after {
  content: '';
  position: absolute;
  left: 18px;       // подстрой под нужное место
  top: -16px;
  width: 13px;
  height: 31px;
  background-image: url('@/assets/svg/connector.svg');
  background-repeat: no-repeat;
  background-size: contain;
}
.layout__child-connector-wrapper.first-child::before {
  content: '';
  position: absolute;
  left: 18px;       // подстрой под нужное место
  top: 0;
  width: 13px;
  height: 31px;
  background-image: url('@/assets/svg/connector-first.svg');
  background-repeat: no-repeat;
  background-size: contain;
}
</style>
